// ResultsDisplay - Affichage premium des résultats de cotation CotIdel
// Organisé par période (Matin / Midi / Soir) avec code couleur :
//   100% → normal  |  50% → ambré  |  0% (gratuit) → gris

import type { AnalyseResult } from '@/pages/analysis/engine';
import { generateExplanation, getAppliedRules, type StepExplanation, type AppliedRule } from '../engineAdapter';

interface ResultsDisplayProps {
  result: AnalyseResult;
  mode: 'coter' | 'expliquer';
}

// ═══ Mapping période → styles ═══
interface PeriodStyle {
  emoji: string;
  headerBg: string;
  headerDot: string;
  headerText: string;
  borderColor: string;
}

function getPeriodStyle(passage: string): PeriodStyle {
  if (passage.includes('Matin')) {
    return {
      emoji: '🌅',
      headerBg: 'bg-amber-50/80',
      headerDot: 'bg-amber-400',
      headerText: 'text-amber-800',
      borderColor: 'border-amber-200/60',
    };
  }
  if (passage.includes('Midi')) {
    return {
      emoji: '☀️',
      headerBg: 'bg-orange-50/80',
      headerDot: 'bg-orange-400',
      headerText: 'text-orange-800',
      borderColor: 'border-orange-200/60',
    };
  }
  if (passage.includes('Soir')) {
    return {
      emoji: '🌙',
      headerBg: 'bg-indigo-50/80',
      headerDot: 'bg-indigo-400',
      headerText: 'text-indigo-800',
      borderColor: 'border-indigo-200/60',
    };
  }
  // Journée / par défaut
  return {
    emoji: '📋',
    headerBg: 'bg-secondary-50/80',
    headerDot: 'bg-secondary-400',
    headerText: 'text-secondary-800',
    borderColor: 'border-secondary-200/60',
  };
}

function getPeriodLabel(passage: string): string {
  if (passage.includes('Matin')) return 'Matin';
  if (passage.includes('Midi')) return 'Midi';
  if (passage.includes('Soir')) return 'Soir';
  return 'Journée';
}

// ═══ Row style selon le taux de facturation ═══
function getTauxStyle(taux: number): string {
  if (taux === 0) return 'bg-foreground-50/50 text-foreground-400';
  if (taux <= 50) return 'bg-amber-50/70';
  return '';
}

function getMontantStyle(taux: number): string {
  if (taux === 0) return 'text-foreground-300';
  if (taux <= 50) return 'text-amber-700';
  return 'text-foreground-900';
}

// ═══ Badge taux ═══
function TauxBadge({ taux }: { taux: number }) {
  if (taux === 0) {
    return (
      <span className="text-[10px] font-medium text-foreground-300 bg-foreground-100/60 px-1.5 py-0.5 rounded whitespace-nowrap">
        <i className="ri-forbid-line text-[10px] mr-0.5"></i>
        Gratuit
      </span>
    );
  }
  if (taux <= 50) {
    return (
      <span className="text-[10px] font-medium text-amber-700 bg-amber-100/80 px-1.5 py-0.5 rounded whitespace-nowrap">
        {taux}%
      </span>
    );
  }
  return null;
}

export default function ResultsDisplay({ result, mode }: ResultsDisplayProps) {
  const isLoading = !result || (result.cotations.length === 0 && result.avertissements.length > 0);

  if (isLoading) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary-50 flex items-center justify-center animate-pulse">
          <i className="ri-brain-line text-primary-500 text-2xl"></i>
        </div>
        <p className="text-sm text-foreground-500">Sélectionnez des soins et lancez l'analyse</p>
      </div>
    );
  }

  // ═══ GROUPER PAR PÉRIODE ═══
  const periodOrder = ['Matin', 'Midi', 'Soir', 'Journée'];
  const grouped: Record<string, typeof result.cotations> = {};

  result.cotations.forEach((c) => {
    const label = getPeriodLabel(c.passage);
    if (!grouped[label]) grouped[label] = [];
    grouped[label].push(c);
  });

  // Trier les clés selon l'ordre défini
  const sortedPeriods = periodOrder.filter((p) => grouped[p]);

  // Total général
  const grandTotal = result.cotations.reduce((sum, c) => sum + c.montant, 0);

  return (
    <div className="space-y-5">
      {/* ═══ CARTE PREMIUM : VOTRE COTATION ═══ */}
      <div className="bg-background-50 border border-background-200/60 rounded-xl overflow-hidden">
        {/* En-tête */}
        <div className="bg-primary-500 px-5 py-4">
          <h3 className="text-base font-bold text-white flex items-center gap-2">
            <i className="ri-shield-check-line text-lg"></i>
            Votre cotation
          </h3>
          <p className="text-xs text-primary-100 mt-0.5">
            {result.cotations.length} acte{result.cotations.length > 1 ? 's' : ''} — {sortedPeriods.length} période{sortedPeriods.length > 1 ? 's' : ''} — Tarifs NGAP 2026
          </p>
        </div>

        {/* ═══ GROUPES PAR PÉRIODE ═══ */}
        {sortedPeriods.map((periodLabel) => {
          const acts = grouped[periodLabel];
          const firstPassage = acts[0]?.passage || periodLabel;
          const style = getPeriodStyle(firstPassage);
          const periodTotal = acts.reduce((sum, c) => sum + c.montant, 0);
          const periodTotalRounded = Math.round(periodTotal * 100) / 100;

          return (
            <div key={periodLabel} className={`border-b ${style.borderColor} last:border-b-0`}>
              {/* En-tête période */}
              <div className={`${style.headerBg} px-5 py-2.5 flex items-center gap-2.5`}>
                <span className="text-base">{style.emoji}</span>
                <span className={`text-sm font-bold ${style.headerText} flex-1`}>
                  {periodLabel}
                </span>
                <span className={`text-xs ${style.headerText} opacity-70`}>
                  {acts.length} acte{acts.length > 1 ? 's' : ''}
                </span>
              </div>

              {/* Lignes des cotations */}
              <div className="divide-y divide-background-100">
                {acts.map((c) => (
                  <div
                    key={c.id}
                    className={`flex items-center px-5 py-3 transition-colors ${getTauxStyle(c.tauxFacturation)}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className={`inline-block px-2 py-0.5 text-xs font-bold rounded whitespace-nowrap ${
                          c.tauxFacturation === 0
                            ? 'bg-foreground-100/60 text-foreground-400'
                            : 'bg-primary-100 text-primary-700'
                        }`}>
                          {c.code}
                        </span>
                        <span className="text-sm font-medium text-foreground-800 truncate">{c.label}</span>
                      </div>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-xs text-foreground-400">Coef. {c.coefficient}</span>
                        <TauxBadge taux={c.tauxFacturation} />
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0 ml-3">
                      <span className={`text-base font-bold ${getMontantStyle(c.tauxFacturation)}`}>
                        {c.montant.toFixed(2).replace('.', ',')} €
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Sous-total période */}
              <div className="px-5 py-2.5 bg-background-50/80 flex items-center justify-between border-t border-background-100">
                <span className="text-xs font-medium text-foreground-500">Sous-total {periodLabel.toLowerCase()}</span>
                <span className="text-sm font-bold text-foreground-700">
                  {periodTotalRounded.toFixed(2).replace('.', ',')} €
                </span>
              </div>
            </div>
          );
        })}

        {/* ═══ LÉGENDE DES COULEURS ═══ */}
        <div className="px-5 py-2.5 bg-background-100/50 border-t border-background-200/50 flex items-center gap-4 flex-wrap">
          <span className="text-[10px] text-foreground-400 uppercase tracking-wider">Légende</span>
          <span className="flex items-center gap-1 text-[10px] text-foreground-600">
            <span className="w-3 h-3 rounded bg-background-50 border border-background-200 inline-block"></span>
            100%
          </span>
          <span className="flex items-center gap-1 text-[10px] text-amber-700">
            <span className="w-3 h-3 rounded bg-amber-50/70 border border-amber-200 inline-block"></span>
            50%
          </span>
          <span className="flex items-center gap-1 text-[10px] text-foreground-400">
            <span className="w-3 h-3 rounded bg-foreground-50/50 border border-foreground-200/60 inline-block"></span>
            Gratuit
          </span>
        </div>

        {/* Majorations */}
        {result.majorations.filter((m) => m.applicable).length > 0 && (
          <div className="border-t border-background-100">
            <div className="px-5 py-2.5 bg-accent-50/50">
              <p className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-2">Majorations</p>
              {result.majorations.filter((m) => m.applicable).map((m, i) => (
                <div key={i} className="flex items-center justify-between py-1.5">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-foreground-700 bg-accent-100 px-1.5 py-0.5 rounded">{m.code}</span>
                    <span className="text-xs text-foreground-600">{m.nom}</span>
                  </div>
                  <span className="text-sm font-semibold text-accent-700">{m.montant}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Total général */}
        <div className="border-t-2 border-primary-100 bg-primary-50/30 px-5 py-4">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-sm font-bold text-foreground-800">Total estimé</span>
              <p className="text-[10px] text-foreground-400 mt-0.5">IFD incluse — IK non calculées (variables selon distance)</p>
            </div>
            <span className="text-2xl font-bold text-primary-600">{result.total.totalGeneral}</span>
          </div>
        </div>
      </div>

      {/* ═══ JAUGE DE RISQUE ═══ */}
      <div className="bg-background-50 border border-background-200/60 rounded-xl p-5">
        <h4 className="text-sm font-bold text-foreground-800 mb-3 flex items-center gap-2">
          <i className="ri-shield-flash-line text-lg" style={{ color: result.risque.couleur === 'green' ? '#16a34a' : result.risque.couleur === 'amber' ? '#d97706' : result.risque.couleur === 'red' ? '#dc2626' : '#9ca3af' }}></i>
          Risque d'indu
        </h4>

        {/* Jauge visuelle */}
        <div className="relative h-4 bg-background-200 rounded-full overflow-hidden mb-3">
          <div
            className="absolute inset-y-0 left-0 rounded-full transition-all duration-700"
            style={{
              width: `${result.risque.score}%`,
              background: result.risque.score >= 85
                ? 'linear-gradient(90deg, #86efac, #22c55e)'
                : result.risque.score >= 65
                  ? 'linear-gradient(90deg, #fde68a, #f59e0b)'
                  : 'linear-gradient(90deg, #fca5a5, #ef4444)',
            }}
          ></div>
        </div>

        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <span className={`text-2xl font-bold ${
              result.risque.score >= 85 ? 'text-green-600' : result.risque.score >= 65 ? 'text-amber-600' : 'text-red-600'
            }`}>
              {result.risque.score}
            </span>
            <span className="text-xs text-foreground-400">/100</span>
          </div>
          <span className={`text-sm font-semibold px-3 py-1 rounded-full ${
            result.risque.score >= 85
              ? 'bg-green-100 text-green-700'
              : result.risque.score >= 65
                ? 'bg-amber-100 text-amber-700'
                : 'bg-red-100 text-red-700'
          }`}>
            {result.risque.score >= 85 ? 'Risque très faible' : result.risque.score >= 65 ? 'Risque moyen' : 'Risque élevé'}
          </span>
        </div>

        {/* Détails du risque */}
        <ul className="space-y-1.5">
          {result.risque.details.map((d, i) => (
            <li key={i} className="flex items-start gap-2">
              <i className={`text-xs mt-0.5 flex-shrink-0 ${
                d.startsWith('ATTENTION') || d.includes('⚠️')
                  ? 'ri-error-warning-line text-amber-500'
                  : 'ri-check-line text-green-500'
              }`}></i>
              <span className="text-xs text-foreground-600 leading-relaxed">{d}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* ═══ CORRECTIONS (si erreurs) ═══ */}
      {result.contradictions.length > 0 && (
        <div className="bg-amber-50 border border-amber-200/60 rounded-xl p-5">
          <h4 className="text-sm font-bold text-foreground-800 mb-3 flex items-center gap-2">
            <i className="ri-lightbulb-line text-amber-500 text-lg"></i>
            Vous pouvez optimiser votre cotation
          </h4>
          <div className="space-y-3">
            {result.contradictions.map((ctr) => (
              <div key={ctr.id} className="bg-background-50 rounded-lg p-3.5 border border-background-200/50">
                <div className="flex items-start gap-2.5">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                    ctr.type === 'error' ? 'bg-red-100' : ctr.type === 'warning' ? 'bg-amber-100' : 'bg-blue-100'
                  }`}>
                    <i className={`text-sm ${
                      ctr.type === 'error' ? 'ri-close-line text-red-600' :
                      ctr.type === 'warning' ? 'ri-error-warning-line text-amber-600' :
                      'ri-information-line text-blue-500'
                    }`}></i>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground-800">{ctr.titre}</p>
                    <p className="text-xs text-foreground-600 mt-0.5 leading-relaxed">{ctr.description}</p>
                    <p className="text-xs text-accent-700 mt-1.5 font-medium">
                      <i className="ri-arrow-right-line text-xs mr-0.5"></i>
                      {ctr.suggestion}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ═══ MODE EXPLIQUER ═══ */}
      {mode === 'expliquer' && (
        <>
          {/* Pourquoi cette cotation ? */}
          <ExplanationSection result={result} />

          {/* Règles appliquées */}
          <RulesSection result={result} />

          {/* Avertissements */}
          <div className="bg-background-50 border border-background-200/60 rounded-xl p-5">
            <h4 className="text-sm font-bold text-foreground-800 mb-3 flex items-center gap-2">
              <i className="ri-information-line text-secondary-500 text-lg"></i>
              Points d'attention
            </h4>
            <ul className="space-y-1.5">
              {result.avertissements.map((a, i) => (
                <li key={i} className="flex items-start gap-2">
                  <i className="ri-arrow-right-line text-accent-500 text-xs mt-0.5 flex-shrink-0"></i>
                  <span className="text-xs text-foreground-600 leading-relaxed">{a}</span>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}

      {/* ═══ SOURCES (toujours visible) ═══ */}
      <div className="text-center">
        <p className="text-[10px] text-foreground-300">
          Tarifs NGAP 2026 — Métropole. Analyse générée le {new Date().toLocaleDateString('fr-FR')}.
          <br />
          <span className="text-foreground-300/70">Aucune donnée conservée. Vérifiez les tarifs sur Ameli.fr.</span>
        </p>
      </div>
    </div>
  );
}

// ═══ SOUS-COMPOSANTS ═══

function ExplanationSection({ result }: { result: AnalyseResult }) {
  const steps = generateExplanation(result);

  return (
    <div className="bg-background-50 border border-background-200/60 rounded-xl p-5">
      <h4 className="text-sm font-bold text-foreground-800 mb-4 flex items-center gap-2">
        <i className="ri-brain-line text-primary-500 text-lg"></i>
        Pourquoi cette cotation ?
      </h4>
      <div className="space-y-4">
        {steps.map((step, i) => (
          <div key={i} className="flex gap-3">
            <div className="flex flex-col items-center flex-shrink-0">
              <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                <i className={`${step.icon} text-primary-600 text-sm`}></i>
              </div>
              {i < steps.length - 1 && (
                <div className="w-px flex-1 bg-primary-200 my-1"></div>
              )}
            </div>
            <div className="pb-4 flex-1">
              <h5 className="text-sm font-semibold text-foreground-800 mb-1">{step.title}</h5>
              <p className="text-xs text-foreground-600 leading-relaxed whitespace-pre-line">{step.detail}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function RulesSection({ result }: { result: AnalyseResult }) {
  const rules = getAppliedRules(result);

  const typeLabel: Record<string, string> = {
    ngap: 'Article NGAP',
    avenant: 'Avenant',
    exception: 'Exception',
    cumul: 'Cumul',
    incompatibilite: 'Incompatibilité',
  };

  const typeColor: Record<string, string> = {
    ngap: 'bg-blue-50 text-blue-700',
    avenant: 'bg-accent-50 text-accent-700',
    exception: 'bg-green-50 text-green-700',
    cumul: 'bg-primary-50 text-primary-700',
    incompatibilite: 'bg-red-50 text-red-700',
  };

  return (
    <div className="bg-background-50 border border-background-200/60 rounded-xl p-5">
      <h4 className="text-sm font-bold text-foreground-800 mb-4 flex items-center gap-2">
        <i className="ri-bookmark-line text-secondary-500 text-lg"></i>
        Règles appliquées
      </h4>
      <div className="space-y-2.5">
        {rules.map((rule, i) => (
          <div key={i} className="flex items-start gap-3 p-3 bg-background-100/50 rounded-lg">
            <div className="flex-shrink-0 mt-0.5">
              <i className={`text-sm ${
                rule.type === 'incompatibilite' ? 'ri-close-circle-line text-red-500' :
                rule.type === 'exception' ? 'ri-star-line text-green-500' :
                'ri-check-line text-primary-500'
              }`}></i>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-0.5">
                <span className="text-xs font-bold text-foreground-700">{rule.article}</span>
                <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${typeColor[rule.type] || 'bg-background-200 text-foreground-500'}`}>
                  {typeLabel[rule.type] || rule.type}
                </span>
              </div>
              <p className="text-xs text-foreground-600 leading-relaxed">{rule.description}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}